/**
 * Formats que le moteur de rendu vidéo (Skia, côté serveur) sait décoder. Même liste que
 * `RenderableField.SupportedImageContentTypes` dans l'API, qui refuse tout autre format
 * dans un champ image.
 */
export const RENDERABLE_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;

/** Plus grand côté conservé à la conversion : au-delà, le rendu réduit l'image de toute façon. */
const MAX_EDGE = 3840;

/** Plafond de l'API pour une image de rendu (`RenderableField.MaxImageSizeBytes`). */
export const MAX_RENDER_IMAGE_BYTES = 25 * 1024 * 1024;

export class UnsupportedImageError extends Error {}

/**
 * Prépare une image pour un champ de template.
 *
 * Un AVIF (format moderne des téléphones récents) est CONVERTI dans le navigateur, qui le
 * décode nativement, plutôt que refusé : l'utilisateur n'a rien à faire, et le serveur n'a
 * pas à embarquer de décodeur supplémentaire exposé à des fichiers non fiables. Le résultat
 * est en WebP (transparence conservée), ou en PNG quand le navigateur ne sait pas encoder
 * le WebP (Safari) — `toBlob` retombe alors sur le PNG, d'où la lecture de `blob.type`.
 */
export async function toRenderableImage(file: File): Promise<File> {
  if ((RENDERABLE_IMAGE_TYPES as readonly string[]).includes(file.type)) {
    return checkSize(file);
  }

  if (!file.type.startsWith('image/') || typeof createImageBitmap !== 'function') {
    throw new UnsupportedImageError('Format non pris en charge : utilisez une image JPEG, PNG, WebP ou AVIF.');
  }

  let bitmap: ImageBitmap;

  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new UnsupportedImageError("Cette image n'a pas pu être lue par votre navigateur.");
  }

  try {
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/webp', 0.92));

    if (!blob || !(RENDERABLE_IMAGE_TYPES as readonly string[]).includes(blob.type)) {
      throw new UnsupportedImageError("La conversion de l'image a échoué.");
    }

    const extension = blob.type === 'image/webp' ? 'webp' : blob.type === 'image/png' ? 'png' : 'jpg';
    const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';

    return checkSize(new File([blob], `${baseName}.${extension}`, { type: blob.type }));
  } finally {
    bitmap.close();
  }
}

function checkSize(file: File): File {
  if (file.size > MAX_RENDER_IMAGE_BYTES) {
    throw new UnsupportedImageError('Image trop volumineuse : 25 Mo maximum.');
  }

  return file;
}
